import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { useSWRConfig } from 'swr';
import { useNavigate } from 'react-router-dom';
import { FaUser, FaSave, FaTimes, FaCamera, FaIdBadge, FaCalendarAlt, FaHeartbeat, FaWeight, FaRulerVertical, FaPhone, FaEnvelope, FaCheck, FaRunning, FaExclamationTriangle, FaRedo, FaSpinner, FaArrowLeft, FaHome, FaCity, FaMapMarkerAlt } from 'react-icons/fa';
import Avatar from '../../Components/Avatar';
import HttpInterceptor from '../../lib/HttpInterceptor';
import { toast } from 'react-toastify';
import { formatDateLocale } from '../../utils/dateFormatter';
import { FILE_CONSTANTS, VALIDATION_CONSTANTS } from '../../constants/appConstants';
import UserContext from '../../Components/UserContext';
import { formatMemberId, formatName, toTitleCase } from '../../utils/dataTransformers';
import {
  validateFile,
  validateDateOfBirth,
  validateNumberRange,
  validateEmail,
  validatePhone,
  validateName
} from '../../utils/validationHelpers';

function StudentProfile() {
  const navigate = useNavigate();
  const { session, setSession } = useContext(UserContext);
  const { mutate } = useSWRConfig();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const MAX_RETRIES = 3;

  // Redirect to trainer profile if user is an approved trainer
  useEffect(() => {
    if (session?.isTrainer && session?.trainerStatus === 'active') {
      navigate('/trainer/trainer-profile', { replace: true });
      return;
    }
  }, [session, navigate]);

  // Default empty student data structure - memoized to prevent infinite loops
  const defaultStudentData = useMemo(() => ({
    id: '',
    name: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    gender: '',
    height: '',
    weight: '',
    bloodGroup: '',
    emergencyContact: {
      name: '',
      phone: '',
      relationship: ''
    },
    address: {
      addressLine1: '',
      addressLine2: '',
      city: '',
      state: '',
      pincode: '',
      country: 'India'
    },
    medicalHistory: {
      conditions: [],
      allergies: [],
      medications: []
    },
    fitnessGoals: [],
    joinDate: new Date().toISOString().split('T')[0],
    status: 'active',
    profileImage: null,
    previousMetrics: {
      weight: '',
      height: '',
      bodyFat: '',
      muscleMass: ''
    },
    currentMetrics: {
      weight: '',
      height: '',
      bodyFat: '',
      muscleMass: ''
    },
    achievements: [],
    preferences: {
      preferredTime: '',
      preferredStyle: '',
      difficultyLevel: ''
    },
    notes: ''
  }), []); // Empty dependency array - this is a constant

  const [student, setStudent] = useState(defaultStudentData);
  const [initialStudentData, setInitialStudentData] = useState(defaultStudentData);

  // Helper function to normalize "Not specified" values to empty strings for form state
  const normalizeProfileData = (data) => {
    const normalized = { ...data };

    // Normalize simple string fields
    const stringFields = ['phone', 'dateOfBirth', 'height', 'weight', 'bloodGroup', 'notes'];
    stringFields.forEach(field => {
      if (normalized[field] === 'Not specified' || normalized[field] === 'Not Specified' ||
        (typeof normalized[field] === 'string' && normalized[field].startsWith('temp_'))) {
        normalized[field] = '';
      }
    });

    // Normalize nested fields
    if (normalized.emergencyContact) {
      Object.keys(normalized.emergencyContact).forEach(key => {
        if (normalized.emergencyContact[key] === 'Not specified' || normalized.emergencyContact[key] === 'Not Specified') {
          normalized.emergencyContact[key] = '';
        }
      });
    }

    // Normalize preferences
    if (normalized.preferences) {
      Object.keys(normalized.preferences).forEach(key => {
        if (normalized.preferences[key] === 'Not specified' || normalized.preferences[key] === 'Not Specified') {
          normalized.preferences[key] = '';
        }
      });
    }


    // Normalize address
    if (normalized.address) {
      Object.keys(normalized.address).forEach(key => {
        if (normalized.address[key] === 'Not specified' || normalized.address[key] === 'Not Specified' || normalized.address[key] === 'not specified') {
          normalized.address[key] = '';
        }
      });
    }

    return normalized;
  };

  // Fetch student profile from backend
  const fetchProfile = useCallback(async (isRetry = false) => {
    try {
      setError(null);
      setIsLoading(true);
      const response = await HttpInterceptor.get('/api/students/profile');
      if (response.status === 200) {
        // Handle both direct object and nested data responses
        const profileData = response.data?.data || response.data || defaultStudentData;
        // Normalize "Not specified" values to empty strings for better UX in edit mode
        const normalizedData = normalizeProfileData(profileData);
        setStudent(normalizedData);
        setInitialStudentData(normalizedData);

        // Calculate attendance metrics from actual data
        const attendanceData = calculateAttendanceFromData(profileData);
        const currentAttendance = `${attendanceData.current}%`;
        const previousAttendance = `${attendanceData.previous}%`;

        setMetrics(prev => ({
          ...prev,
          attendanceProgress: calculateAttendanceProgress(currentAttendance, previousAttendance),
          classesCompleted: attendanceData.attended,
          classesProgress: calculateClassesProgress(attendanceData.attended, Math.max(0, attendanceData.attended - 2))
        }));
      }
    } catch (err) {
      console.error('Error fetching profile:', err);
      const errorMessage = err.response?.data?.message || 'Failed to load profile';

      if (retryCount < MAX_RETRIES && !isRetry) {
        setError({
          message: errorMessage,
          retryable: true
        });
        toast.error('Failed to load profile. Please try again.');
      } else {
        setError({
          message: 'Unable to load profile. Please check your connection and try again later.',
          retryable: false
        });
        toast.error('Unable to load profile');
      }
    } finally {
      setIsLoading(false);
    }
  }, [retryCount, defaultStudentData]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const handleRetry = () => {
    setRetryCount(prev => prev + 1);
    fetchProfile(true);
  };
  const [profileImagePreview, setProfileImagePreview] = useState(null);
  const [profileImageFile, setProfileImageFile] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [errors, setErrors] = useState({});
  // Calculate attendance from student data
  const calculateAttendanceFromData = (studentData) => {
    // Try to get attendance from students array if available
    if (studentData?.students && studentData.students.length > 0) {
      const totalSessions = studentData.students.reduce((sum, s) => sum + (s.attendance?.totalSessions || 0), 0);
      const attendedSessions = studentData.students.reduce((sum, s) => sum + (s.attendance?.attendedSessions || 0), 0);
      const currentPercentage = totalSessions > 0 ? Math.round((attendedSessions / totalSessions) * 100) : 0;
      // Don't estimate previous - use actual historical data if available, otherwise show 0
      const previousPercentage = 0; // Should be calculated from historical attendance data
      return { current: currentPercentage, previous: previousPercentage, total: totalSessions, attended: attendedSessions };
    }
    // Fallback: try to get from attendance object directly
    if (studentData?.attendance) {
      const current = studentData.attendance.attendancePercentage || 0;
      // Don't estimate previous - use actual historical data if available
      const previous = 0; // Should be calculated from historical attendance data
      return {
        current,
        previous,
        total: studentData.attendance.totalSessions || 0,
        attended: studentData.attendance.attendedSessions || 0
      };
    }
    return { current: 0, previous: 0, total: 0, attended: 0 };
  };

  const [metrics, setMetrics] = useState({
    weightProgress: calculateProgress(initialStudentData.weight, initialStudentData.previousMetrics.weight),
    flexibilityProgress: calculateFlexibilityProgress(initialStudentData.preferences?.difficultyLevel, initialStudentData.preferences?.difficultyLevel),
    attendanceProgress: '',
    classesCompleted: 0,
    classesProgress: ''
  });

  // Calculate weight progress
  function calculateProgress(currentWeight, previousWeight) {
    // Extract numeric values from strings that might contain units
    const current = parseFloat(String(currentWeight).replace(/[^\d.-]/g, ''));
    const previous = parseFloat(String(previousWeight).replace(/[^\d.-]/g, ''));

    if (isNaN(current) || isNaN(previous)) {
      return 'No change from last month';
    }

    const difference = previous - current;

    if (difference > 0) return `↓ ${difference.toFixed(1)}kg from last month`;
    if (difference < 0) return `↑ ${Math.abs(difference).toFixed(1)}kg from last month`;
    return 'No change from last month';
  }

  // Calculate flexibility progress
  function calculateFlexibilityProgress(currentLevel, previousLevel) {
    const levels = ['beginner', 'intermediate', 'advanced'];
    const currentIndex = levels.indexOf(currentLevel?.toLowerCase() || 'beginner');
    const previousIndex = levels.indexOf(previousLevel?.toLowerCase() || 'beginner');

    if (currentIndex > previousIndex) return `↑ Improved from ${previousLevel || 'beginner'}`;
    if (currentIndex < previousIndex) return `↓ Declined from ${previousLevel || 'beginner'}`;
    return `Maintained ${currentLevel || 'beginner'}`;
  }

  // Calculate attendance progress
  function calculateAttendanceProgress(current, previous) {
    const currentVal = parseInt(current);
    const previousVal = parseInt(previous);
    const difference = currentVal - previousVal;

    if (difference > 0) return `↑ ${difference}% from last month`;
    if (difference < 0) return `↓ ${Math.abs(difference)}% from last month`;
    return 'Same as last month';
  }

  // Calculate classes completed progress
  function calculateClassesProgress(current, previous) {
    const difference = current - previous;
    if (difference > 0) return `↑ ${difference} more than last month`;
    if (difference < 0) return `↓ ${Math.abs(difference)} less than last month`;
    return 'Same as last month';
  }

  // Update metrics when student data changes
  useEffect(() => {
    if (student.weight && student.weight !== initialStudentData.weight) {
      setMetrics(prev => ({
        ...prev,
        weightProgress: calculateProgress(student.weight, initialStudentData.previousMetrics?.weight || student.weight)
      }));
    }
    if (student.preferences?.difficultyLevel && student.preferences?.difficultyLevel !== initialStudentData.preferences?.difficultyLevel) {
      setMetrics(prev => ({
        ...prev,
        flexibilityProgress: calculateFlexibilityProgress(
          student.preferences?.difficultyLevel,
          initialStudentData.preferences?.difficultyLevel
        )
      }));
    }
  }, [student.weight, student.preferences?.difficultyLevel, initialStudentData]);

  // Using imported validation helpers - create local aliases for clarity
  const validateEmailLocal = validateEmail;
  const validatePhoneLocal = validatePhone;

  const validateNumber = (value, min, max) => {
    return validateNumberRange(value, min, max);
  };

  const validateField = (name, value) => {
    let error = '';

    switch (name) {
      case 'email':
        if (!value) {
          error = 'Email is required';
        } else if (!validateEmailLocal(value)) {
          error = 'Please enter a valid email address';
        }
        break;
      case 'phone':
        if (!value) {
          error = 'Phone number is required';
        } else if (!validatePhoneLocal(value)) {
          error = 'Please enter a valid 10-digit phone number (starting with 6-9)';
        }
        break;
      case 'dateOfBirth':
        if (!value) {
          error = 'Date of Birth is required';
        } else {
          const validation = validateDateOfBirth(value);
          if (!validation.isValid) {
            error = validation.errors.join(', ');
          }
        }
        break;
      case 'height':
        if (!value) {
          error = 'Height is required';
        } else if (!validateNumberRange(value, VALIDATION_CONSTANTS.HEIGHT_MIN, VALIDATION_CONSTANTS.HEIGHT_MAX)) {
          error = `Height should be between ${VALIDATION_CONSTANTS.HEIGHT_MIN}-${VALIDATION_CONSTANTS.HEIGHT_MAX} cm`;
        }
        break;
      case 'weight':
        if (!value) {
          error = 'Weight is required';
        } else if (!validateNumberRange(value, VALIDATION_CONSTANTS.WEIGHT_MIN, VALIDATION_CONSTANTS.WEIGHT_MAX)) {
          error = `Weight should be between ${VALIDATION_CONSTANTS.WEIGHT_MIN}-${VALIDATION_CONSTANTS.WEIGHT_MAX} kg`;
        }
        break;
      case 'name':
        if (!value || !value.trim()) {
          error = 'Name is required';
        } else if (!validateName(value)) {
          if (value.trim().length < VALIDATION_CONSTANTS.NAME_MIN_LENGTH) {
            error = `Name must be at least ${VALIDATION_CONSTANTS.NAME_MIN_LENGTH} characters`;
          } else if (value.trim().length > VALIDATION_CONSTANTS.NAME_MAX_LENGTH) {
            error = `Name must be at most ${VALIDATION_CONSTANTS.NAME_MAX_LENGTH} characters`;
          } else {
            error = 'Name can only contain letters, spaces, hyphens, and apostrophes';
          }
        }
        break;
      case 'address.addressLine1':
        if (!value || !value.trim()) {
          error = 'Address Line 1 is required';
        }
        break;
      case 'address.city':
        if (!value || !value.trim()) {
          error = 'City is required';
        }
        break;
      case 'address.state':
        if (!value || !value.trim()) {
          error = 'State is required';
        }
        break;
      case 'address.pincode':
        if (!value) {
          error = 'Pincode is required';
        } else if (!/^[0-9]{6}$/.test(value)) {
          error = 'Enter a valid 6-digit pincode';
        }
        break;
      default:
        break;
    }

    setErrors(prev => ({ ...prev, [name]: error }));
    return !error;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Handle nested properties like 'preferences.difficultyLevel'
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setStudent(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setStudent(prev => ({ ...prev, [name]: value }));
    }

    validateField(name, value);
    setHasChanges(true);

    // Auto-fetch city and state from pincode
    if (name === 'address.pincode' && value.length === 6 && /^[0-9]{6}$/.test(value)) {
      fetchCityStateFromPincode(value);
    }
  };

  const fetchCityStateFromPincode = async (pincode) => {
    try {
      const response = await fetch(`https://api.postalpincode.in/pincode/${pincode}`);
      const data = await response.json();
      if (data && data[0] && data[0].Status === 'Success') {
        const { District, State, Country } = data[0].PostOffice[0];
        setStudent(prev => ({
          ...prev,
          address: {
            ...prev.address,
            city: District,
            state: State,
            country: Country || 'India'
          }
        }));
        // validate fields again to remove errors if any
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors['address.city'];
          delete newErrors['address.state'];
          return newErrors;
        });
      } else {
        toast.error('Invalid Pincode');
      }
    } catch (error) {
      console.error('Error fetching pincode details:', error);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file using helper function
      const validation = validateFile(file, {
        maxSize: FILE_CONSTANTS.MAX_IMAGE_SIZE,
        allowedTypes: FILE_CONSTANTS.ALLOWED_IMAGE_TYPES,
      });

      if (!validation.isValid) {
        setErrors(prev => ({
          ...prev,
          profileImage: validation.errors.join(', ')
        }));
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        // Validate that the result is actually a data URL
        if (typeof reader.result === 'string' && reader.result.startsWith('data:image/')) {
          setProfileImagePreview(reader.result);
          setProfileImageFile(file);
          setErrors(prev => ({ ...prev, profileImage: '' }));
          setHasChanges(true);
        } else {
          setErrors(prev => ({ ...prev, profileImage: 'Failed to read image file' }));
        }
      };
      reader.onerror = () => {
        setErrors(prev => ({ ...prev, profileImage: 'Error reading image file' }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSaveChanges = async () => {
    // Validate all fields before saving
    let isValid = true;
    const newErrors = {};

    if (!validateField('name', student.name)) isValid = false;
    if (!validateField('email', student.email)) isValid = false;
    if (!validateField('phone', student.phone)) isValid = false;
    if (!validateField('dateOfBirth', student.dateOfBirth)) isValid = false;
    if (!validateField('height', student.height)) isValid = false;
    if (!validateField('weight', student.weight)) isValid = false;
    if (!validateField('address.addressLine1', student.address?.addressLine1)) isValid = false;
    if (!validateField('address.city', student.address?.city)) isValid = false;
    if (!validateField('address.state', student.address?.state)) isValid = false;
    if (!validateField('address.pincode', student.address?.pincode)) isValid = false;

    if (isValid) {
      try {
        // Upload profile image first (if selected)
        if (profileImageFile) {
          const formData = new FormData();
          formData.append('profileImage', profileImageFile);

          const uploadRes = await HttpInterceptor.post('/api/students/profile/image', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });

          const uploadedUrl = uploadRes?.data?.profileImage;
          if (uploadedUrl) {
            // Bust caches (service worker + browser) by changing URL.
            const cacheBustedUrl = `${uploadedUrl}${uploadedUrl.includes('?') ? '&' : '?'}v=${Date.now()}`;
            setStudent(prev => ({ ...prev, profileImage: cacheBustedUrl }));
          }

          setProfileImagePreview(null);
          setProfileImageFile(null);
        }

        const response = await HttpInterceptor.put('/api/students/profile', student);
        if (response.status === 200) {
          // Update session in context to ensure profile checks pass in other components
          if (setSession) {
            setSession(prev => ({
              ...prev,
              name: student.name,
              mobile: student.phone, // phone in form is mobile in DB/session
              gender: student.gender,
              address: student.address
            }));
          }
          
          // Force refresh SWR cache for all components using Guard or session
          mutate('/auth/session');
          
          toast.success('Profile updated successfully');
          setHasChanges(false);
          setEditMode(false);
        }
      } catch (error) {
        console.error('Error updating profile:', error);
        toast.error('Failed to update profile');
      }
    } else {
      // Scroll to first error
      const firstError = Object.keys(errors).find(key => errors[key]);
      if (firstError) {
        document.getElementById(firstError)?.scrollIntoView?.({ behavior: 'smooth' });
      }
    }
  };

  const handleCancelChanges = () => {
    setStudent(initialStudentData);
    setProfileImagePreview(null);
    setProfileImageFile(null);
    setHasChanges(false);
    setEditMode(false);
    setErrors({});
    if (initialStudentData.weight) {
      setMetrics({
        weightProgress: calculateProgress(initialStudentData.weight, initialStudentData.previousMetrics?.weight || initialStudentData.weight),
        flexibilityProgress: calculateFlexibilityProgress(initialStudentData.preferences?.difficultyLevel, initialStudentData.preferences?.difficultyLevel),
        attendanceProgress: '',
        classesCompleted: attendanceData.attended || 0,
        classesProgress: calculateClassesProgress(attendanceData.attended || 0, Math.max(0, (attendanceData.attended || 0) - 2))
      });
    }
  };

  const handleKeyDown = (e, action) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      action();
    }
  };

  const MetricCard = ({ title, value, trend, icon }) => (
    <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="text-sm font-medium text-gray-500">{title}</h4>
          <p className="text-xl font-bold text-gray-800 mt-1">{value}</p>
        </div>
        {icon && <div className="text-green-600" aria-hidden="true">{icon}</div>}
      </div>
      <p
        className={`text-sm mt-2 ${trend.includes('↑') ? 'text-green-600' : trend.includes('↓') ? 'text-red-600' : 'text-gray-500'}`}
        aria-label={`Trend: ${trend}`}
      >
        {trend}
      </p>
    </div>
  );

  // Helper function to normalize value for edit mode (remove "Not specified" and empty strings)
  const normalizeValueForEdit = (value) => {
    if (!value || value === 'Not specified' || value === 'Not Specified' || value === 'not specified') {
      return '';
    }
    // Handle temp_ mobile numbers
    if (typeof value === 'string' && value.startsWith('temp_')) {
      return '';
    }
    return value;
  };

  // Helper function to get placeholder text based on field type
  const getPlaceholder = (label, type) => {
    if (type === 'date') return 'Select date (dd-mm-yyyy)';
    if (type === 'email') return 'Enter email address';
    if (type === 'tel') return 'Enter 10-digit phone number (starting with 6-9)';
    if (type === 'number') {
      if (label.toLowerCase().includes('height')) return 'Enter height in cm (100-250)';
      if (label.toLowerCase().includes('weight')) return 'Enter weight in kg';
      return `Enter ${label.toLowerCase()}`;
    }
    return `Enter ${label.toLowerCase()}`;
  };

  const renderField = (label, name, value, icon, type = 'text', options = null) => {
    // Normalize value for edit mode
    const normalizedValue = editMode ? normalizeValueForEdit(value) : value;
    const displayValue = editMode ? normalizedValue : (value && value !== 'Not specified' && !value.startsWith('temp_') ? value : 'Not specified');

    return (
      <div className="mb-4">
        <label htmlFor={name} className="flex items-center text-gray-600 mb-2">
          <span className="mr-2 text-green-600" aria-hidden="true">{icon}</span>
          {label}
          {(name === 'name' || name === 'email' || name === 'phone' || name === 'gender' || name === 'dateOfBirth' || name === 'height' || name === 'weight' || name.startsWith('address.')) && (
            <span className="text-red-500 ml-1" title="Required field">*</span>
          )}
        </label>
        {editMode ? (
          <>
            {options ? (
              <select
                id={name}
                name={name}
                value={normalizedValue || ''}
                onChange={handleInputChange}
                className="w-full p-2 border-b-2 border-green-300 focus:border-green-500 focus:outline-none"
                aria-required={type === 'email' || type === 'tel'}
              >
                {options.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type={type}
                id={name}
                name={name}
                value={normalizedValue || ''}
                placeholder={getPlaceholder(label, type)}
                onChange={handleInputChange}
                onBlur={() => validateField(name, normalizedValue)}
                className={`w-full p-2 border-b-2 ${errors[name] ? 'border-red-500' : 'border-green-300'} focus:border-green-500 focus:outline-none`}
                aria-required={type === 'email' || type === 'tel'}
                aria-invalid={!!errors[name]}
                aria-describedby={`${name}-error`}
              />
            )}
            {errors[name] && (
              <p id={`${name}-error`} className="text-red-500 text-sm mt-1">
                {errors[name]}
              </p>
            )}
          </>
        ) : (
          <div
            id={name}
            className="text-lg font-medium text-gray-800 pl-6"
            tabIndex="0"
            aria-label={`${label}: ${displayValue}`}
          >
            {displayValue === 'Not specified' ? (
              <span className="text-gray-400 italic">{displayValue}</span>
            ) : (
              type === 'date' ? displayValue : toTitleCase(displayValue)
            )}
          </div>
        )}
      </div>
    );
  };

  const renderTextArea = (label, name, value, icon) => {
    // Normalize value for edit mode
    const normalizedValue = editMode ? normalizeValueForEdit(value) : value;
    const displayValue = editMode ? normalizedValue : (value || 'Not specified');

    return (
      <div className="mb-6">
        <label htmlFor={`textarea-${name}`} className="flex items-center text-gray-600 mb-2">
          <span className="mr-2 text-green-600" aria-hidden="true">{icon}</span>
          {label}
        </label>
        {editMode ? (
          <>
            <textarea
              id={`textarea-${name}`}
              name={name}
              value={normalizedValue || ''}
              placeholder={`Enter ${label.toLowerCase()}`}
              onChange={handleInputChange}
              rows="4"
              className="w-full p-3 border-2 border-green-300 rounded-lg focus:border-green-500 focus:outline-none"
              aria-label={`Edit ${label.toLowerCase()}`}
            />
            {errors[name] && (
              <p className="text-red-500 text-sm mt-1">
                {errors[name]}
              </p>
            )}
          </>
        ) : (
          <div
            id={`textarea-${name}`}
            className="text-gray-700 pl-6 border-l-2 border-green-200"
            tabIndex="0"
            aria-label={`${label}: ${displayValue}`}
          >
            {displayValue === 'Not specified' ? (
              <span className="text-gray-400 italic">{displayValue}</span>
            ) : (
              displayValue
            )}
          </div>
        )}
      </div>
    );
  };

  // Loading State
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8 flex items-center justify-center">
        <div className="text-center">
          <FaSpinner className="text-6xl mx-auto text-blue-600 animate-spin mb-4" />
          <p className="text-gray-600">Loading your profile...</p>
        </div>
      </div>
    );
  }

  // Error State
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8 flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="text-red-400 mb-4">
            <FaExclamationTriangle className="text-6xl mx-auto" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Unable to Load Profile</h3>
          <p className="text-gray-500 mb-6">{error.message}</p>
          {error.retryable && (
            <button
              onClick={handleRetry}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 mx-auto"
            >
              <FaRedo />
              Retry
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto">
        {/* Back Button */}
        <button
          onClick={() => navigate('/student/home')}
          className="flex items-center text-gray-600 hover:text-green-600 mb-4 transition-colors"
          aria-label="Go back to dashboard"
        >
          <FaArrowLeft className="mr-2" aria-hidden="true" />
          Back to Dashboard
        </button>

        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          {/* Profile Header */}
          <div className="bg-gradient-to-r from-green-600 to-green-800 px-8 py-6 text-white relative">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h1 className="text-2xl font-bold">My Profile</h1>
                <p className="text-green-100">Manage your personal information</p>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full">
                  <div
                    className={`w-3 h-3 rounded-full ${student.status === 'active' ? 'bg-green-600' : 'bg-red-600'}`}
                    aria-hidden="true"
                  ></div>
                  <span className="text-sm text-black">
                    {student.status ? (student.status.charAt(0).toUpperCase() + student.status.slice(1)) : 'Active'}
                  </span>
                </div>

                {!editMode ? (
                  <button
                    onClick={() => setEditMode(true)}
                    onKeyDown={(e) => handleKeyDown(e, () => setEditMode(true))}
                    className="flex items-center gap-2 bg-white text-green-700 px-4 py-2 rounded-full hover:bg-green-50 transition-colors"
                    aria-label="Edit profile"
                  >
                    <FaSave aria-hidden="true" /> Edit Profile
                  </button>
                ) : (
                  <div className="flex gap-2">
                    <button
                      onClick={handleSaveChanges}
                      onKeyDown={(e) => handleKeyDown(e, handleSaveChanges)}
                      disabled={!hasChanges}
                      className={`flex items-center gap-2 px-4 py-2 rounded-full ${hasChanges ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`}
                      aria-label={hasChanges ? "Save changes" : "No changes to save"}
                    >
                      <FaSave aria-hidden="true" /> Save Changes
                    </button>
                    <button
                      onClick={handleCancelChanges}
                      onKeyDown={(e) => handleKeyDown(e, handleCancelChanges)}
                      className="flex items-center gap-2 bg-gray-200 text-gray-700 px-4 py-2 rounded-full hover:bg-gray-300 transition-colors"
                      aria-label="Cancel changes"
                    >
                      <FaTimes aria-hidden="true" /> Cancel
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Profile Content */}
          <div className="p-6 md:flex gap-8">
            {/* Left Column - Profile Image */}
            <div className="md:w-1/3 mb-8 md:mb-0 flex flex-col items-center">
              <div className="relative mb-6">
                <div className="w-48 h-48 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden border-4 border-white shadow-md relative">
                  {editMode && profileImagePreview ? (
                    <img
                      src={profileImagePreview}
                      alt="Student profile preview"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <Avatar
                      src={student.profileImage}
                      name={student.name}
                      size={192}
                      alt="Student profile"
                    />
                  )}
                </div>
                {editMode && (
                  <>
                    <label
                      className="absolute -bottom-2 right-2 bg-green-600 text-white p-3 rounded-full cursor-pointer hover:bg-green-700 shadow-lg transition-all flex items-center justify-center z-10"
                      tabIndex="0"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          document.getElementById('profile-image-upload').click();
                        }
                      }}
                      aria-label="Change profile picture"
                    >
                      <FaCamera size={16} aria-hidden="true" />
                      <input
                        id="profile-image-upload"
                        type="file"
                        className="hidden"
                        accept="image/*"
                        onChange={handleImageChange}
                      />
                    </label>
                    {errors.profileImage && (
                      <p className="text-red-500 text-sm text-center mt-2">
                        {errors.profileImage}
                      </p>
                    )}
                  </>
                )}
              </div>

              <div className="text-center w-full">
                <h2 className="text-2xl font-bold text-gray-800 mb-1">{formatName(student.name)}</h2>
                <p className="text-green-600 font-medium mb-4">Member</p>

                <div className="bg-gray-50 p-4 rounded-lg w-full mb-4">
                  <div className="flex items-center justify-center text-gray-500 text-sm">
                    <FaCalendarAlt className="mr-2 text-green-600" aria-hidden="true" />
                    <span>Member since {formatDateLocale(student.joinDate)}</span>
                  </div>
                </div>

                <div className="bg-green-50 p-4 rounded-lg w-full">
                  <div className="flex items-center justify-center">
                    <FaIdBadge className="text-green-600 mr-3 text-xl" aria-hidden="true" />
                    <div>
                      <div className="text-xs text-gray-500">Member ID</div>
                      <div className="font-bold text-green-800">{formatMemberId(student.id, student.name)}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Profile Details */}
            <div className="md:w-2/3">
              <div className="space-y-4 max-h-[calc(100vh-200px)] overflow-y-auto pr-2">
                {renderField('Full Name', 'name', editMode ? student.name : formatName(student.name), <FaUser />)}
                {renderField('Email', 'email', student.email, <FaEnvelope />, 'email')}
                {renderField('Phone', 'phone', student.phone, <FaPhone />, 'tel')}
                {renderField('Date of Birth', 'dateOfBirth', student.dateOfBirth, <FaCalendarAlt />, 'date')}
                {renderField('Gender', 'gender', student.gender, <FaUser />, 'select', [
                  { value: 'male', label: 'Male' },
                  { value: 'female', label: 'Female' },
                  { value: 'other', label: 'Other' }
                ])}
                {renderField('Height (cm)', 'height', student.height, <FaRulerVertical />)}
                {renderField('Weight (kg)', 'weight', student.weight, <FaWeight />)}
                {renderField('Blood Group', 'bloodGroup', student.bloodGroup, <FaHeartbeat />)}
                {renderField('Difficulty Level', 'preferences.difficultyLevel', student.preferences?.difficultyLevel, <FaHeartbeat />, 'select', [
                  { value: 'beginner', label: 'Beginner' },
                  { value: 'intermediate', label: 'Intermediate' },
                  { value: 'advanced', label: 'Advanced' }
                ])}

                {/* Address Section */}
                <section aria-labelledby="address-heading" className="py-2 border-t mt-4">
                  <h3 id="address-heading" className="text-lg font-medium mb-3 flex items-center">
                    <FaHome className="text-green-600 mr-2" aria-hidden="true" />
                    Address Details
                  </h3>
                  {renderField('Address Line 1', 'address.addressLine1', student.address?.addressLine1, <FaHome />)}
                  {renderField('Address Line 2', 'address.addressLine2', student.address?.addressLine2, <FaHome />)}
                  {renderField('Pincode', 'address.pincode', student.address?.pincode, <FaMapMarkerAlt />, 'text', null)}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {renderField('City', 'address.city', student.address?.city, <FaCity />)}
                    {renderField('State', 'address.state', student.address?.state, <FaMapMarkerAlt />)}
                  </div>
                </section>

                {/* Health Metrics Section */}
                <section aria-labelledby="health-metrics-heading">
                  <h3 id="health-metrics-heading" className="text-lg font-medium mb-4 flex items-center">
                    <FaHeartbeat className="text-green-600 mr-2" aria-hidden="true" />
                    Health Metrics
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <MetricCard
                      title="Weight"
                      value={`${student.weight} kg`}
                      trend={metrics.weightProgress}
                      icon={<FaWeight />}
                    />
                    <MetricCard
                      title="Difficulty Level"
                      value={student.preferences?.difficultyLevel ? toTitleCase(student.preferences.difficultyLevel) : 'Not set'}
                      trend={metrics.flexibilityProgress}
                      icon={<FaRunning />}
                    />
                    <MetricCard
                      title="Attendance"
                      value={(() => {
                        const attendanceData = calculateAttendanceFromData(student);
                        return attendanceData.current > 0 ? `${attendanceData.current}%` : 'No data';
                      })()}
                      trend={metrics.attendanceProgress || 'No data available'}
                    />
                    <MetricCard
                      title="Classes Completed"
                      value={metrics.classesCompleted}
                      trend={metrics.classesProgress}
                    />
                  </div>
                </section>

                {/* Goals Tracking Section */}
                <section aria-labelledby="goals-heading">
                  <h3 id="goals-heading" className="text-lg font-medium mb-4 flex items-center">
                    <FaHeartbeat className="text-green-600 mr-2" aria-hidden="true" />
                    Current Goals
                  </h3>
                  <div className="space-y-3" role="list">
                    {student.fitnessGoals && student.fitnessGoals.map((goal, i) => (
                      <div
                        key={i}
                        className="flex items-start bg-gray-50 p-3 rounded-lg"
                        role="listitem"
                      >
                        <div className="mt-1 mr-3">
                          <input
                            type="checkbox"
                            id={`goal-${i}`}
                            className="h-4 w-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                            aria-label={`Mark goal '${goal}' as complete`}
                          />
                        </div>
                        <label htmlFor={`goal-${i}`} className="flex-1 text-gray-700">
                          {goal}
                        </label>
                      </div>
                    ))}
                  </div>
                </section>

                {renderTextArea('Additional Notes', 'notes', student.notes || '', <FaHeartbeat />)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default StudentProfile;